import axios from "axios";
import cogoToast from "cogo-toast";

const { createSlice, createAsyncThunk, isAnyOf } = require("@reduxjs/toolkit");

// get all clients
export const getClientProducts = createAsyncThunk(
  "auth/getClientProducts",
  async (
    {
      seasonalCategoryId,
      setProducts,
      setTotalRecords,
      viewIndex = 0,
      viewSize = 10,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState();

      let params = {
        viewIndex,
        viewSize,
      };

      if (auth?.selectedCategories?.length > 0) {
        params = { ...params, productCategoryIds: auth?.selectedCategories };
      } else {
        params = { ...params, seasonalCategoryId };
      }

      const response = await axios.post("/services/getClientProducts", params);

      if (response.status === 200) {
        const { productAndCategoryMembers, totalRecords } =
          response?.data?.data;
        setProducts(productAndCategoryMembers || []);
        setTotalRecords(totalRecords || 0);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// get all product categories
export const getClientProductCatagory = createAsyncThunk(
  "auth/getClientProductCatagory",
  async ({ setCategories }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const agreementId = auth?.client?.agreementId || "";
      const partyId = auth?.client?.partyId || "";
      const response = await axios.get(
        `/services/getClientProductCatagory?inParams=${encodeURIComponent(
          JSON.stringify({ agreementId, partyId })
        )}`
      );

      if (response.status === 200) {
        setCategories(response?.data?.data?.productCategoryIds || []);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// get all product categories
export const getClientProductMatrix = createAsyncThunk(
  "auth/getClientProductMatrix",
  async ({ data, setProductMatrixData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/services/getClientProductMatrix",
        data
      );

      if (response.status === 200) {
        setProductMatrixData(response?.data?.data?.productMatrixList);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// get product content
export const getProductContent = createAsyncThunk(
  "auth/getProductContent",
  async ({ productId, setProductContent }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/getProductContent", {
        productId,
      });

      if (response.status === 200) {
        setProductContent(response?.data?.data?.productContent);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Add or update item to cart
export const addOrUpdateItemToCart = createAsyncThunk(
  "auth/addOrUpdateItemToCart",
  async ({ productList, callback }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/addOrUpdateItemToCart", {
        productList,
      });

      if (response.status === 200) {
        cogoToast.success("Cart updated successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
        if (callback) callback();
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Create client order
export const createClientOrder = createAsyncThunk(
  "auth/createClientOrder",
  async ({ callback }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/createClientOrder", {});

      if (response.status === 200) {
        cogoToast.success("Order created successfully!", {
          position: "top-right",
          hideAfter: 5,
        });
        if (callback) callback(response?.data?.data?.orderId);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Clear cart
export const clearCart = createAsyncThunk(
  "auth/clearCart",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post("/services/clearCart", {});

      if (response.status === 200) {
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Get Categories
export const getProductCategoryRollups = createAsyncThunk(
  "auth/getProductCategoryRollups",
  async (
    { setCategoryRollups, parentProductCategoryId },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get(
        `/services/getProductCategoryRollups?inParams=${encodeURIComponent(
          JSON.stringify({ parentProductCategoryId })
        )}`
      );

      if (response.status === 200) {
        setCategoryRollups(response?.data?.data?.categories || []);
      }
      return response?.data?.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  products: [],
  isLoading: false,
};

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    setProducts(state, action) {
      state.products = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(
          getClientProducts.pending,
          getProductContent.pending,
          addOrUpdateItemToCart.pending,
          createClientOrder.pending,
          getClientProductMatrix.pending
        ),
        (state) => {
          state.isLoading = true;
        }
      )
      .addMatcher(
        isAnyOf(
          getClientProducts.fulfilled,
          getClientProducts.rejected,
          getProductContent.fulfilled,
          getProductContent.rejected,
          addOrUpdateItemToCart.fulfilled,
          addOrUpdateItemToCart.rejected,
          createClientOrder.fulfilled,
          createClientOrder.rejected,
          getClientProductMatrix.fulfilled,
          getClientProductMatrix.rejected
        ),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export const productSelector = (state) => state.product;

export const { setProducts } = productSlice.actions;
export default productSlice.reducer;
